import type {Permission} from '@hconnect/apiclient'
import {
  type UpmPlantsContextValue,
  type UpmPlant,
  type PlantIdsPerCountryMap,
  type PlantUpmId,
  getPlantIdsPerCountryMap,
  getPlantName
} from '@hconnect/upm'

import {allApps} from './config'
import {PLANT_ID} from './consts'
import {MaintainAppConfig, MaintainAppOption, maintainAllApps} from './maintain.types'
import type {AppConfig, AppPlusPlant, AppPlantSelectOption, WhiteList, PlantId} from './types'

export type Env = 'prod' | 'qa' | 'uat'

const checkIsPlantNotWhitelisted = (
  appConfig: AppConfig | MaintainAppConfig,
  plantUpmId: PlantUpmId
) => {
  if (!appConfig.plant?.whiteList) {
    return false
  }
  return !appConfig.plant.whiteList.includes(plantUpmId)
}

const checkIsPlantBlacklisted = (
  appConfig: AppConfig | MaintainAppConfig,
  plantUpmId: PlantUpmId
) => {
  if (!appConfig.plant?.blackList) {
    return false
  }
  return appConfig.plant.blackList.includes(plantUpmId)
}

export const parseDataScope = (dataScope: string) => {
  try {
    const parsedScope = JSON.parse(dataScope) as Partial<{plantId: string; countryId: string}>
    return parsedScope
  } catch (error) {
    throw new Error('BUG: Error during parsing permission dataScope')
  }
}

export const addToWhiteList = (
  env: Env,
  whiteList: WhiteList,
  appConfig: AppConfig,
  plantId: PlantId,
  plantName: string
) => {
  const appName = appConfig.name

  const url = appConfig.url[env].replace(PLANT_ID, plantId)

  const option: AppPlantSelectOption = {
    appName,
    plantId,
    plantName,
    url
  }

  if (!whiteList.optionsPerApp[appName]) {
    whiteList.optionsPerApp[appName] = [option]
  } else if (
    !whiteList.optionsPerApp[appName]?.find(
      (current: AppPlantSelectOption) => current.plantId === plantId
    )
  ) {
    whiteList.optionsPerApp[appName]?.push(option)
  }

  if (!whiteList.optionsPerPlant[plantId]) {
    whiteList.optionsPerPlant[plantId] = [option]
  } else if (
    !whiteList.optionsPerPlant[plantId].find(
      (current: AppPlantSelectOption) => current.appName === appName
    )
  ) {
    whiteList.optionsPerPlant[plantId].push(option)
  }

  whiteList.optionsMap.set(appName + plantId, option)
}

const addToMaintainWhiteList = (
  whiteList: WhiteList,
  appConfig: MaintainAppConfig,
  plantId: PlantId,
  plantName: string
) => {
  const appName = appConfig.name

  const option: MaintainAppOption = {
    appName,
    plantId,
    plantName
  }

  if (!whiteList.maintainApps[appName]) {
    whiteList.maintainApps[appName] = [option]
  } else if (
    !whiteList.maintainApps[appName]?.find(
      (current: MaintainAppOption) => current.plantId === plantId
    )
  ) {
    whiteList.maintainApps[appName]?.push(option)
  }

  if (!whiteList.maintainApps[plantId]) {
    whiteList.maintainApps[plantId] = [option]
  } else if (
    !whiteList.maintainApps[plantId].find(
      (current: AppPlantSelectOption) => current.appName === appName
    )
  ) {
    whiteList.maintainApps[plantId].push(option)
  }
}

const processPlantLevelPermission = (
  upmPlants: UpmPlantsContextValue,
  plantId: string,
  whiteList: WhiteList,
  appConfig: AppConfig | MaintainAppConfig,
  env: Env
) => {
  const upmPlant = upmPlants[plantId] as UpmPlant | undefined

  const plantUpmId = upmPlant?.upmId ?? ''
  const plantUpmName = getPlantName(upmPlants, plantId)

  // This block of code is for tracking if we deal with permission
  // which has a scope of a plant that is not present within upm
  if (upmPlant === undefined) {
    if (!whiteList.missedPlantIds) {
      whiteList.missedPlantIds = [plantId]
    } else if (!whiteList.missedPlantIds.includes(plantId)) {
      whiteList.missedPlantIds.push(plantId)
    }
  }

  if (checkIsPlantNotWhitelisted(appConfig, plantUpmId)) return

  if (checkIsPlantBlacklisted(appConfig, plantUpmId)) return

  return 'url' in appConfig
    ? addToWhiteList(env, whiteList, appConfig, plantId, plantUpmName)
    : addToMaintainWhiteList(whiteList, appConfig, plantId, plantUpmName)
}

const addPlantsBasedOnUserPermission = (
  appConfig: AppConfig | MaintainAppConfig,
  userPermission: Permission,
  upmPlants: UpmPlantsContextValue,
  whiteList: WhiteList,
  env: Env,
  plantIdsPerCountryMap: PlantIdsPerCountryMap
) => {
  const isUserPermissionInAppPermission = appConfig.whiteListedPermissionTypes.includes(
    userPermission.permissionType
  )
  if (!isUserPermissionInAppPermission) return

  // Parsing permission dataScope
  const parsedDataScope = parseDataScope(userPermission.dataScope)

  const plantId = parsedDataScope.plantId
  const countryId = parsedDataScope.countryId

  if (!countryId) return

  // Processing specific plant permission
  if (plantId) {
    return processPlantLevelPermission(upmPlants, plantId, whiteList, appConfig, env)
  }

  // Processing country level permission --> we need to add all related plantIds to the whitelist
  plantIdsPerCountryMap[countryId]?.forEach((plantId) => {
    return processPlantLevelPermission(upmPlants, plantId, whiteList, appConfig, env)
  })
}

/**
 * Gives a list of all the app + plant combinations that a user has access to
 */
export const whiteListByPermission = (
  env: Env = 'prod',
  usersPermissions: Permission[],
  upmPlants: UpmPlantsContextValue
): WhiteList => {
  const apps = [...allApps, ...maintainAllApps]

  const plantIdsPerCountryMap = getPlantIdsPerCountryMap(upmPlants)

  const whiteList: WhiteList = {
    optionsPerApp: {},
    optionsPerPlant: {},
    optionsMap: new Map<AppPlusPlant, AppPlantSelectOption>(),
    missedPlantIds: [],
    maintainApps: {}
  }

  // Keeping the same order of the apps by iterating over apps first and then usersPermissions
  apps.forEach((appConfig) => {
    usersPermissions.forEach((userPermission) =>
      addPlantsBasedOnUserPermission(
        appConfig,
        userPermission,
        upmPlants,
        whiteList,
        env,
        plantIdsPerCountryMap
      )
    )
  })

  return whiteList
}
